import { useCallback, useEffect, useState } from "react";
import { MoonStars, ShieldWarning, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function OvernightReviewPage() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/overnight-review").then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ checkins: [], open: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="flex justify-center py-24"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  const review = async (t) => {
    try {
      await api.post("/admin/tasks/mark-done", { ref_id: t.ref_id, kind: t.kind });
      toast.success("Reviewed — cleared.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8" data-testid="overnight-review">
      <div>
        <h1 className="font-heading text-3xl tracking-tight text-[#4A235A]">Overnight check-in review</h1>
        <p className="mt-1 text-sm text-stone-500">
          Caregivers on overnight duty check in between 12am–5am. Safety-critical clients who miss one escalate here
          immediately; administrative misses queue for a morning call. Nothing is emailed — the office acts from this page.
        </p>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
          <ShieldWarning size={14} /> Needs office review ({data.open.length})
        </p>
        {data.open.length === 0 ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700" data-testid="overnight-all-clear">
            <CheckCircle size={16} weight="fill" /> Nothing open — every overnight was covered.
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="overnight-open-list">
            {data.open.map((t) => (
              <li key={t.id} className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${t.kind === "overnight_escalation" ? "border-[#C0392B]/30 bg-[#C0392B]/5" : "border-stone-200"}`}
                data-testid={`overnight-task-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${t.kind === "overnight_escalation" ? "text-[#C0392B]" : "text-stone-800"}`}>{t.title}</p>
                  <p className="text-xs text-stone-500">{t.note}</p>
                </div>
                <button onClick={() => review(t)}
                  className="rounded-full bg-[#4A235A] px-3 py-1 text-[11px] font-semibold text-white hover:bg-stone-900"
                  data-testid={`overnight-review-${t.id}`}>
                  Mark reviewed
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
          <MoonStars size={14} /> Check-ins — last 14 days
        </p>
        {data.checkins.length === 0 ? (
          <p className="mt-3 text-sm text-stone-400" data-testid="overnight-no-checkins">No overnight check-ins recorded yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-stone-100" data-testid="overnight-checkin-list">
            {data.checkins.map((c, i) => (
              <li key={c.id || i} className="flex flex-wrap items-center gap-2 py-2" data-testid={`overnight-checkin-${i}`}>
                <span className="w-32 text-xs font-medium text-stone-600">{c.punched_et}</span>
                <span className="text-sm text-stone-800">{c.name}</span>
                <span className="text-xs text-stone-500">with {c.client_name} — client {c.client_status}</span>
                <span className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] font-semibold ${c.in_window ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"}`}>
                  {c.in_window ? `${c.window} window ✓` : "outside window"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
